import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { BalanceService } from '../../services/balance';
import { Balance, BalanceTransaction, Withdrawal } from '../../types';
import { safeArray } from '../../utils/money';

// 异步actions
export const fetchBalance = createAsyncThunk(
  'balance/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await BalanceService.getBalance();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取余额失败');
    }
  }
);

export const fetchBalanceHistory = createAsyncThunk(
  'balance/fetchHistory',
  async (params: { currency?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await BalanceService.getBalanceHistory(
        params.currency,
        params.page,
        params.limit
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取余额历史失败');
    }
  }
);

export const fetchWithdrawals = createAsyncThunk(
  'balance/fetchWithdrawals',
  async (params: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
    try {
      const response = await BalanceService.getWithdrawals(
        params.page,
        params.limit,
        params.status
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取提现记录失败');
    }
  }
);

export const createWithdrawal = createAsyncThunk(
  'balance/createWithdrawal',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await BalanceService.createWithdrawal(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '创建提现失败');
    }
  }
);

export const cancelWithdrawal = createAsyncThunk(
  'balance/cancelWithdrawal',
  async (id: string, { rejectWithValue }) => {
    try {
      await BalanceService.cancelWithdrawal(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '取消提现失败');
    }
  }
);

interface BalanceState {
  balance: Balance | null;
  history: {
    data: BalanceTransaction[];
    pagination: any;
    loading: boolean;
  };
  withdrawals: {
    data: Withdrawal[];
    pagination: any;
    loading: boolean;
  };
  loading: boolean;
  creating: boolean;
  error: string | null;
}

const initialState: BalanceState = {
  balance: null,
  history: {
    data: [],
    pagination: null,
    loading: false,
  },
  withdrawals: {
    data: [],
    pagination: null,
    loading: false,
  },
  loading: false,
  creating: false,
  error: null,
};

const balanceSlice = createSlice({
  name: 'balance',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // 获取余额
    builder
      .addCase(fetchBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload;
      })
      .addCase(fetchBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // 获取余额历史
    builder
      .addCase(fetchBalanceHistory.pending, (state) => {
        state.history.loading = true;
        state.error = null;
      })
      .addCase(fetchBalanceHistory.fulfilled, (state, action) => {
        state.history.loading = false;
        state.history.data = safeArray<BalanceTransaction>(action.payload.data);
        state.history.pagination = action.payload.pagination;
      })
      .addCase(fetchBalanceHistory.rejected, (state, action) => {
        state.history.loading = false;
        state.error = action.payload as string;
      });

    // 获取提现记录
    builder
      .addCase(fetchWithdrawals.pending, (state) => {
        state.withdrawals.loading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawals.fulfilled, (state, action) => {
        state.withdrawals.loading = false;
        state.withdrawals.data = safeArray<Withdrawal>(action.payload.data);
        state.withdrawals.pagination = action.payload.pagination;
      })
      .addCase(fetchWithdrawals.rejected, (state, action) => {
        state.withdrawals.loading = false;
        state.error = action.payload as string;
      });

    // 创建提现
    builder
      .addCase(createWithdrawal.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createWithdrawal.fulfilled, (state, action) => {
        state.creating = false;
        state.withdrawals.data.unshift(action.payload);
      })
      .addCase(createWithdrawal.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // 取消提现
    builder
      .addCase(cancelWithdrawal.pending, (state) => {
        state.error = null;
      })
      .addCase(cancelWithdrawal.fulfilled, (state, action) => {
        const index = state.withdrawals.data.findIndex(w => w.id === action.payload);
        if (index !== -1) {
          state.withdrawals.data[index].status = 'CANCELLED' as any;
        }
      })
      .addCase(cancelWithdrawal.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = balanceSlice.actions;
export default balanceSlice.reducer;
